import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Box } from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import VideocamIcon from '@material-ui/icons/Videocam';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { CameraTile } from '@vgps/livekit_player';

// Build-time tunable: defer 'recovering' overlay. undefined → CameraTile default.
const RECOVERING_OVERLAY_DELAY_MS = (() => {
  const v = Number(process.env.REACT_APP_LIVEKIT_RECOVERING_OVERLAY_MS);
  return Number.isFinite(v) && v >= 0 ? v : undefined;
})();

const CAMERA_CHANNELS = ['1', '2', '3', '4'];
const LIVE_CAMERA_CHANNELS = new Set(['1', '2']);

export const hasCameraDevice = vehicle => {
  const deviceType =
    vehicle?.device_type || vehicle?.vehicle?.device_type || vehicle?.vehicle_type;
  return ['VGPS_4CAM', 'VGPS_2CAM'].includes(deviceType);
};

const getMaxCameraCount = deviceType => (deviceType === 'VGPS_2CAM' ? 2 : 4);

const getCameraViewOptions = deviceType => {
  const maxCameraCount = getMaxCameraCount(deviceType);
  return [
    { label: '1x1', count: 1 },
    { label: '1x2', count: 2 },
    ...(maxCameraCount === 4 ? [{ label: '1x4', count: 4 }] : [])
  ];
};

const CameraViewOptionIcon = ({ count }) => (
  <Box
    className={`tracking-camera-layout-icon tracking-camera-layout-icon-${count}`}
    aria-hidden="true"
  >
    {Array.from({ length: count }).map((_, index) => (
      <Box className="tracking-camera-layout-bar" key={`${count}-${index}`} />
    ))}
  </Box>
);

const CameraPlaceholder = () => (
  <Box className="tracking-camera-tile-placeholder">
    <VideocamIcon className="tracking-camera-empty-icon" />
  </Box>
);

const TrackingCameraToolbar = ({
  isVisible,
  isMuted,
  isFullscreen,
  onToggleMute,
  onScreenshot,
  onToggleFullscreen
}) => (
  <Box
    className={`tracking-camera-toolbar ${isVisible ? 'tracking-camera-toolbar-visible' : ''
      }`}
  >
    <Box className="tracking-camera-toolbar-group">
      <Box
        component="button"
        type="button"
        className="tracking-camera-tool-button"
        onClick={onToggleMute}
        aria-label={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
        title={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
      >
        {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </Box>
    </Box>
    <Box className="tracking-camera-toolbar-group">
      <Box
        component="button"
        type="button"
        className="tracking-camera-tool-button"
        onClick={onScreenshot}
        aria-label="Chụp ảnh camera"
        title="Chụp ảnh"
      >
        <PhotoCameraIcon />
      </Box>
      <Box
        component="button"
        type="button"
        className="tracking-camera-tool-button"
        onClick={onToggleFullscreen}
        aria-label={isFullscreen ? 'Thoát toàn màn hình camera' : 'Mở toàn màn hình camera'}
        title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </Box>
    </Box>
  </Box>
);

/**
 * TrackingCameraTile — wraps the SDK's CameraTile and drives its imperative
 * handle from the tracking-view custom toolbar (mute / screenshot / fullscreen).
 *
 * State comes from CameraTile.onMediaStateChange (SDK-owned). Actions go
 * through CameraTileHandle (also SDK-owned). No DOM polling, no
 * MutationObserver — the previous querySelector('video') / 1.5 s setInterval
 * pattern broke whenever the SDK changed its overlay markup.
 */
const TrackingCameraTile = ({ deviceSerial, channel }) => {
  const tileHandleRef = useRef(null);
  const containerRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const fullscreenCleanupTimerRef = useRef(null);
  const wasFullscreenRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const fsTimeoutRef = useRef(null);
  const [mediaState, setMediaState] = useState({
    isLive: false,
    muted: true,
    volume: 0,
    isFullscreen: false,
    quality: 'sd',
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (containerRef.current) {
      setPortalTarget(containerRef.current.firstChild);
    }
  }, []);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (!isFullscreen) {
      setIsFullscreenHovered(false);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
        fsTimeoutRef.current = null;
      }
      return;
    }

    const handleMouseMove = () => {
      setIsFullscreenHovered(true);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
      fsTimeoutRef.current = setTimeout(() => {
        setIsFullscreenHovered(false);
      }, 2500);
    };

    const handleMouseLeave = () => {
      setIsFullscreenHovered(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    handleMouseMove();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (fsTimeoutRef.current) {
        clearTimeout(fsTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  const handleMediaStateChange = useCallback((next) => {
    setMediaState(next);
  }, []);

  // Reproduce the existing "returning from fullscreen" CSS animation. The dock
  // and tile briefly get a returning class so style.css can animate the layout
  // back to the docked position. We trigger this off the SDK's fullscreen
  // event instead of subscribing to fullscreenchange ourselves — the SDK is
  // already the source of truth for fullscreen ownership.
  const handleFullscreenChange = useCallback((isFs) => {
    const tileEl = containerRef.current;
    if (!tileEl) return;
    if (isFs) {
      wasFullscreenRef.current = true;
      tileEl.classList.remove('tracking-camera-tile-returning');
      tileEl.closest('.tracking-camera-dock')?.classList.remove('tracking-camera-dock-returning');
      if (fullscreenCleanupTimerRef.current) {
        window.clearTimeout(fullscreenCleanupTimerRef.current);
        fullscreenCleanupTimerRef.current = null;
      }
      return;
    }
    if (!wasFullscreenRef.current) return;
    wasFullscreenRef.current = false;
    const dockEl = tileEl.closest('.tracking-camera-dock');
    tileEl.classList.add('tracking-camera-tile-returning');
    dockEl?.classList.add('tracking-camera-dock-returning');
    window.requestAnimationFrame(() => { tileEl.getBoundingClientRect(); });
    fullscreenCleanupTimerRef.current = window.setTimeout(() => {
      tileEl.classList.remove('tracking-camera-tile-returning');
      dockEl?.classList.remove('tracking-camera-dock-returning');
      fullscreenCleanupTimerRef.current = null;
    }, 450);
  }, []);

  useEffect(() => {
    handleFullscreenChange(isFullscreen);
  }, [handleFullscreenChange, isFullscreen]);

  useEffect(() => () => {
    if (fullscreenCleanupTimerRef.current) {
      window.clearTimeout(fullscreenCleanupTimerRef.current);
    }
  }, []);

  const handleToggleMute = useCallback((event) => {
    event.stopPropagation();
    tileHandleRef.current?.setMuted(!mediaState.muted);
  }, [mediaState.muted]);

  const handleScreenshot = useCallback(async (event) => {
    event.stopPropagation();
    await tileHandleRef.current?.downloadScreenshot();
  }, []);

  const handleToggleFullscreen = useCallback(async (event) => {
    event.stopPropagation();
    const tileEl = containerRef.current;
    if (!tileEl) return;
    if (document.fullscreenElement === tileEl) {
      await document.exitFullscreen?.();
    } else {
      await tileEl.requestFullscreen?.();
    }
  }, []);

  const toolbar = (
    <TrackingCameraToolbar
      isVisible={mediaState.isLive && (isFullscreen ? isFullscreenHovered : isHovered)}
      isMuted={mediaState.muted}
      isFullscreen={isFullscreen}
      onToggleMute={handleToggleMute}
      onScreenshot={handleScreenshot}
      onToggleFullscreen={handleToggleFullscreen}
    />
  );

  return (
    <Box
      ref={containerRef}
      className={`tracking-camera-tile ${mediaState.isLive ? 'tracking-camera-tile-live' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CameraTile
        ref={tileHandleRef}
        deviceSerial={deviceSerial}
        channelId={channel}
        title={`CH${channel}`}
        height="100%"
        onMediaStateChange={handleMediaStateChange}
        onFullscreenChange={handleFullscreenChange}
        recoveringOverlayDelayMs={RECOVERING_OVERLAY_DELAY_MS}
      />
      {portalTarget ? ReactDOM.createPortal(toolbar, portalTarget) : toolbar}
    </Box>
  );
};

const TrackingCameraPanel = ({ deviceSerial, viewCount, deviceType }) => {
  const maxCameraCount = getMaxCameraCount(deviceType);
  const visibleChannels = CAMERA_CHANNELS
    .slice(0, Math.min(viewCount, maxCameraCount))
    .reverse();

  return (
    <Box className="tracking-camera-panel tracking-camera-panel-vertical">
      {visibleChannels.map(channel => (
        <Box className="tracking-camera-slot" key={channel}>
          {deviceSerial && LIVE_CAMERA_CHANNELS.has(channel) ? (
            <TrackingCameraTile deviceSerial={deviceSerial} channel={channel} />
          ) : (
            <CameraPlaceholder />
          )}
        </Box>
      ))}
    </Box>
  );
};

const TrackingCameraControls = ({ viewCount, deviceType, onChangeViewCount }) => {
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const closeMenuTimerRef = useRef(null);
  const viewOptions = getCameraViewOptions(deviceType);
  const reversedViewOptions = [...viewOptions].reverse();

  const openViewMenu = useCallback(() => {
    if (closeMenuTimerRef.current) {
      window.clearTimeout(closeMenuTimerRef.current);
      closeMenuTimerRef.current = null;
    }
    setIsViewMenuOpen(true);
  }, []);

  const closeViewMenu = useCallback(() => {
    if (closeMenuTimerRef.current) {
      window.clearTimeout(closeMenuTimerRef.current);
    }
    closeMenuTimerRef.current = window.setTimeout(() => {
      setIsViewMenuOpen(false);
      closeMenuTimerRef.current = null;
    }, 180);
  }, []);

  useEffect(() => {
    if (!isViewMenuOpen) return undefined;

    const handleClickOutside = event => {
      if (!menuRef.current?.contains(event.target)) {
        setIsViewMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isViewMenuOpen]);

  useEffect(() => () => {
    if (closeMenuTimerRef.current) {
      window.clearTimeout(closeMenuTimerRef.current);
    }
  }, []);

  return (
    <Box
      className="tracking-camera-view-control"
      ref={menuRef}
      onMouseEnter={openViewMenu}
      onMouseLeave={closeViewMenu}
    >
      <Box
        component="button"
        type="button"
        className="tracking-camera-grid-button"
        aria-haspopup="menu"
        aria-expanded={isViewMenuOpen}
        aria-label="Chọn bố cục camera"
        title="Lưới"
        onFocus={openViewMenu}
      >
        <AppsIcon style={{ fontSize: 22 }} />
      </Box>
      {isViewMenuOpen && (
        <Box className="tracking-camera-view-menu">
          {reversedViewOptions.map(option => (
            <Box
              key={option.label}
              component="button"
              type="button"
              className={`tracking-camera-view-option ${viewCount === option.count ? 'active' : ''
                }`}
              onClick={() => {
                onChangeViewCount(option.count);
                setIsViewMenuOpen(false);
              }}
              aria-pressed={viewCount === option.count}
              aria-label={`Hiển thị ${option.count} camera`}
              title={`Hiển thị ${option.count} camera`}
            >
              <CameraViewOptionIcon count={option.count} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const TrackingCameraDock = ({
  deviceSerial,
  deviceType,
  viewCount,
  showMenu,
  isMapFullscreen = false,
  leftOffset = 0,
  onChangeViewCount
}) => (
  <Box
    className={`tracking-camera-dock tracking-camera-dock-vertical ${showMenu ? 'tracking-camera-dock-menu-open' : ''
      } ${isMapFullscreen && viewCount === 4 ? 'tracking-camera-dock-fill-height' : ''}`}
    style={{
      '--camera-count': Math.min(viewCount, getMaxCameraCount(deviceType)),
      left: `${leftOffset}px`
    }}
  >
    <TrackingCameraPanel
      deviceSerial={deviceSerial}
      deviceType={deviceType}
      viewCount={viewCount}
    />
    <TrackingCameraControls
      deviceType={deviceType}
      viewCount={viewCount}
      onChangeViewCount={onChangeViewCount}
    />
  </Box>
);

export default TrackingCameraDock;
